import { useMemo, type ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  formatDateInTimezone,
  getCurrentTimezoneOffset,
  getUserTimezone,
  timeFromNowDetailed,
} from '@/util/dateUtils';

export interface TimestampTooltipProps {
  timestamp: string | Date | number;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * TimestampTooltip component that wraps existing relative timestamp displays with rich tooltips.
 * Shows full precision relative time, absolute UTC time, user's local timezone on hover.
 * Uses the shadcn tooltip variant system with pure business logic for timestamp processing.
 *
 * @param timestamp - The timestamp for tooltip calculations (ISO string, Date object, or numeric timestamp)
 * @param children - The existing relative time text to display (component handles its own styling)
 * @param side - Position of the tooltip relative to the trigger (default: 'top')
 */
function TimestampTooltip({ timestamp, children, side = 'top' }: TimestampTooltipProps) {
  // Get user's timezone and offset
  const userTimezone = useMemo(() => getUserTimezone(), []);
  const timezoneOffset = useMemo(() => getCurrentTimezoneOffset(), []);

  // Generate tooltip content
  const tooltipContent = useMemo(() => {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return null; // No tooltip for invalid dates
    }

    // Detailed relative time
    const detailedTimeFromNow = timeFromNowDetailed(date);

    return (
      <div className="flex w-64 flex-col items-center justify-center gap-3 p-3">
        {/* Relative timestamp header */}
        <div className="self-stretch font-sans text-xs leading-5 opacity-80">
          {detailedTimeFromNow}
        </div>

        {/* UTC timestamp line */}
        <div className="flex items-center gap-1.5 self-stretch">
          <div className="flex w-12 items-center justify-center gap-4 rounded bg-foreground-muted p-1 opacity-80">
            <span className="font-mono text-xs leading-4">UTC</span>
          </div>
          <span className="flex-1 text-xs leading-5">
            {formatDateInTimezone(date, 'UTC', 'MMM d, yyyy')}
          </span>
          <span className="font-mono text-xs leading-4 opacity-70">
            {formatDateInTimezone(date, 'UTC', 'hh:mm:ss a')}
          </span>
        </div>

        {/* Local timestamp line */}
        <div className="flex items-center gap-1.5 self-stretch">
          <div className="flex w-12 items-center justify-center gap-4 rounded bg-foreground-muted p-1 opacity-80">
            <span className="font-mono text-xs leading-4">{timezoneOffset}</span>
          </div>
          <span className="flex-1 text-xs leading-5">
            {formatDateInTimezone(date, userTimezone, 'MMM d, yyyy')}
          </span>
          <span className="font-mono text-xs leading-4 opacity-70">
            {formatDateInTimezone(date, userTimezone, 'hh:mm:ss a')}
          </span>
        </div>
      </div>
    );
  }, [timestamp, userTimezone, timezoneOffset]);

  // If no valid tooltip content, just render children without tooltip
  if (!tooltipContent) {
    return <>{children}</>;
  }

  return (
    <Tooltip delayDuration={500}>
      <TooltipTrigger asChild>
        <span className="inline-flex max-w-full items-center">{children}</span>
      </TooltipTrigger>
      <TooltipContent variant="timestamp" side={side}>
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  );
}

TimestampTooltip.displayName = 'TimestampTooltip';

export default TimestampTooltip;
